import { useEffect, useRef, useState } from 'react';
import { api, type AudioTrack } from '../../api/client';
import { IconButton } from '../ui';
import { useUiStore } from '../../store/uiStore';
import styles from './BgmPanel.module.css';

const VOL_KEY = 'gmbinder:vol';

export interface BgmPanelProps {
  slug: string;
}

/** Background-music panel: list, play, loop, volume, upload (drag&drop) and delete tracks. */
export function BgmPanel({ slug }: BgmPanelProps) {
  const toast = useUiStore((s) => s.toast);
  const [collapsed, setCollapsed] = useState(false);
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [current, setCurrent] = useState<AudioTrack | null>(null);
  const [loop, setLoop] = useState(true);
  const [dragging, setDragging] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    try {
      const { tracks: t } = await api.listAudio(slug);
      setTracks(t);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    refresh();
    const saved = Number(localStorage.getItem(VOL_KEY));
    if (audioRef.current && !Number.isNaN(saved) && saved > 0) audioRef.current.volume = saved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const play = (track: AudioTrack) => {
    setCurrent(track);
    setLoop(track.loop ?? true);
    requestAnimationFrame(() => {
      if (audioRef.current) {
        audioRef.current.src = track.url;
        void audioRef.current.play().catch(() => undefined);
      }
    });
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const { tracks: t } = await api.uploadAudio(slug, Array.from(files));
      setTracks(t);
      toast('Brani caricati', 'success');
    } catch {
      toast('Upload fallito', 'danger');
    }
  };

  const remove = async (file: string) => {
    try {
      const { tracks: t } = await api.deleteAudio(slug, file);
      setTracks(t);
      if (current?.file === file) setCurrent(null);
    } catch {
      toast('Eliminazione fallita', 'danger');
    }
  };

  return (
    <aside className={[styles.panel, collapsed ? styles.collapsed : undefined].filter(Boolean).join(' ')}>
      <div className={styles.header}>
        <h3 className={styles.title}>🎵 BGM</h3>
        <span className={styles.toggle}>
          <IconButton
            label={collapsed ? 'Espandi' : 'Comprimi'}
            size="sm"
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? '🎵' : '▸'}
          </IconButton>
        </span>
      </div>

      <div className={styles.body}>
        <div className={styles.now}>
          <div className={styles.nowTitle}>{current?.title ?? 'Nessuna traccia'}</div>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            ref={audioRef}
            className={styles.audio}
            controls
            loop={loop}
            preload="none"
            onEnded={() => {
              if (loop || !current) return;
              const idx = tracks.findIndex((t) => t.file === current.file);
              const next = tracks[idx + 1];
              if (next) play(next);
            }}
            onVolumeChange={(e) =>
              localStorage.setItem(VOL_KEY, String((e.target as HTMLAudioElement).volume))
            }
          />
          <div className={styles.controls}>
            <label>
              <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> 🔁
              Loop
            </label>
          </div>
        </div>

        <div
          className={[styles.dropzone, dragging ? styles.dragging : undefined]
            .filter(Boolean)
            .join(' ')}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void upload(e.dataTransfer.files);
          }}
        >
          ⬆️ Trascina i brani qui o clicca per scegliere
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.mp3,.ogg,.wav,.m4a,.opus,.flac,.aac"
            multiple
            hidden
            onChange={(e) => void upload(e.target.files)}
          />
        </div>

        <div className={styles.list}>
          {tracks.length === 0 ? (
            <div className={styles.muted}>Nessun brano. Carica qui sopra.</div>
          ) : (
            tracks.map((t) => (
              <div
                key={t.file}
                className={[styles.track, current?.file === t.file ? styles.trackActive : undefined]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => play(t)}
              >
                <span aria-hidden>{current?.file === t.file ? '♪' : '▸'}</span>
                <span className={styles.trackName}>{t.title}</span>
                <button
                  className={styles.del}
                  aria-label={`Elimina ${t.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void remove(t.file);
                  }}
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </aside>
  );
}
